import { useState, useContext, useEffect, useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import axios from "axios";
import {
  Container,
  Typography,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import { UserContext } from "../providers/userProvider";

const API_BASE = "https://us-central1-whitesides-family-blog.cloudfunctions.net";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const RECURRING_OPTIONS = [
  { value: "none", label: "One-time" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
];

const MAX_PERSONAL = 10;
const MAX_GROUP = 4;

export const Calendar = () => {
  const { user } = useContext(UserContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);

  const [addOpen, setAddOpen] = useState(false);
  const [addType, setAddType] = useState("personal");
  const [form, setForm] = useState({ title: "", message: "", date: "", time: "", recurring: "none" });
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);

  const [detailEvent, setDetailEvent] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  useEffect(() => {
    if (user.groupId) fetchEvents();
  }, [user.groupId]);

  const fetchEvents = async () => {
    setLoadingEvents(true);
    try {
      const res = await axios.get(`${API_BASE}/getEvents?groupId=${user.groupId}`);
      setEvents(res.data.events);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingEvents(false);
    }
  };

  const myPersonal = events.filter((e) => e.type === "personal" && e.createdBy === user.id);
  const groupRecurring = events.filter((e) => e.type === "group");

  const personalRemaining = MAX_PERSONAL - myPersonal.length;
  const groupRemaining = MAX_GROUP - groupRecurring.length;

  const handleOpenAdd = (type) => {
    setAddType(type);
    setForm({ title: "", message: "", date: "", time: "", recurring: type === "group" ? "weekly" : "none" });
    setFormError(null);
    setAddOpen(true);
  };

  const handleSaveEvent = async () => {
    if (!form.title.trim()) { setFormError("Title is required."); return; }
    if (!form.message.trim()) { setFormError("Message is required."); return; }
    if (!form.date) { setFormError("Date is required."); return; }
    if (!form.time) { setFormError("Time is required."); return; }
    if (addType === "personal" && personalRemaining <= 0) { setFormError("You've reached your personal event limit."); return; }
    if (addType === "group" && groupRemaining <= 0) { setFormError("This group has reached its group reminder limit."); return; }

    setSaving(true);
    setFormError(null);
    try {
      const scheduledAt = new Date(`${form.date}T${form.time}`).toISOString();
      const res = await axios.post(`${API_BASE}/createEvent`, {
        groupId: user.groupId,
        createdBy: user.id,
        type: addType,
        title: form.title,
        message: form.message,
        scheduledAt,
        recurring: addType === "group" ? form.recurring : "none",
      });
      setEvents((prev) => [...prev, res.data]);
      setAddOpen(false);
    } catch (e) {
      setFormError(e.response?.data?.error || "Failed to create event.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await axios.post(`${API_BASE}/deleteEvent`, { eventId: detailEvent.id, userId: user.id });
      setEvents((prev) => prev.filter((e) => e.id !== detailEvent.id));
      setDetailEvent(null);
    } catch (e) {
      setDeleteError(e.response?.data?.error || "Failed to delete event.");
    } finally {
      setDeleting(false);
    }
  };

  // Build calendar grid
  const calendarDays = useMemo(() => {
    const firstDay = new Date(viewYear, viewMonth, 1).getDay();
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    return cells;
  }, [viewYear, viewMonth]);

  const eventsForDay = (day) => {
    if (!day) return [];
    return events.filter((e) => {
      const d = new Date(e.scheduledAt);
      return d.getFullYear() === viewYear && d.getMonth() === viewMonth && d.getDate() === day;
    });
  };

  const monthLabel = new Date(viewYear, viewMonth).toLocaleString("default", { month: "long", year: "numeric" });

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear((y) => y - 1); }
    else setViewMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear((y) => y + 1); }
    else setViewMonth((m) => m + 1);
  };

  const formatTime = (iso) => {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Group Calendar
      </Typography>

      {/* Quota buttons */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => handleOpenAdd("personal")}
          disabled={personalRemaining <= 0}
        >
          + Personal Event ({personalRemaining} of {MAX_PERSONAL} remaining)
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => handleOpenAdd("group")}
          disabled={groupRemaining <= 0}
        >
          + Group Reminder ({groupRemaining} of {MAX_GROUP} remaining)
        </Button>
      </Box>

      {/* Month nav */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1 }}>
        <IconButton onClick={prevMonth} size="small"><ChevronLeftIcon /></IconButton>
        <Typography variant="h6" sx={{ minWidth: 200, textAlign: "center" }}>{monthLabel}</Typography>
        <IconButton onClick={nextMonth} size="small"><ChevronRightIcon /></IconButton>
      </Box>

      {loadingEvents ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress sx={{ color: "text.primary" }} />
        </Box>
      ) : (
        <>
          {/* Day headers */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", mb: 0.5 }}>
            {DAYS.map((d) => (
              <Typography key={d} variant="caption" fontWeight="bold" sx={{ textAlign: "center", py: 0.5 }}>
                {d}
              </Typography>
            ))}
          </Box>

          {/* Calendar grid */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            {calendarDays.map((day, idx) => {
              const dayEvents = eventsForDay(day);
              const isToday =
                day &&
                today.getFullYear() === viewYear &&
                today.getMonth() === viewMonth &&
                today.getDate() === day;
              return (
                <Box
                  key={idx}
                  sx={{
                    minHeight: 80,
                    borderRight: (idx + 1) % 7 !== 0 ? "1px solid" : "none",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    p: 0.5,
                    backgroundColor: day ? "background.paper" : "action.hover",
                  }}
                >
                  {day && (
                    <>
                      <Typography
                        variant="caption"
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 24,
                          height: 24,
                          borderRadius: "50%",
                          backgroundColor: isToday ? "secondary.main" : "transparent",
                          color: isToday ? "primary.main" : "text.primary",
                          fontWeight: isToday ? "bold" : "normal",
                          mb: 0.5,
                        }}
                      >
                        {day}
                      </Typography>
                      {isMobile ? (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.4, mt: 0.25 }}>
                          {dayEvents.map((ev) => (
                            <Box
                              key={ev.id}
                              onClick={() => { setDetailEvent(ev); setDeleteError(null); }}
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                backgroundColor: ev.type === "group" ? "#2e7d32" : "#1565c0",
                                cursor: "pointer",
                              }}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                          {dayEvents.map((ev) => (
                            <Chip
                              key={ev.id}
                              label={ev.title}
                              size="small"
                              onClick={() => { setDetailEvent(ev); setDeleteError(null); }}
                              sx={{
                                fontSize: "0.6rem",
                                height: 18,
                                cursor: "pointer",
                                backgroundColor: ev.type === "group" ? "#2e7d32" : "#1565c0",
                                color: "#fff",
                                "& .MuiChip-label": { px: 0.75 },
                              }}
                            />
                          ))}
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              );
            })}
          </Box>

          {/* Legend */}
          <Box sx={{ display: "flex", gap: 2, mt: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "#1565c0" }} />
              <Typography variant="caption">Personal</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "#2e7d32" }} />
              <Typography variant="caption">Group Reminder</Typography>
            </Box>
          </Box>

          {/* Group reminders list */}
          {groupRecurring.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom>Group Reminders</Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {groupRecurring.map((ev) => (
                  <Box
                    key={ev.id}
                    onClick={() => { setDetailEvent(ev); setDeleteError(null); }}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.5,
                      p: 1.5,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      cursor: "pointer",
                      "&:hover": { backgroundColor: "action.hover" },
                    }}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        backgroundColor: "#2e7d32",
                        mt: 0.6,
                        flexShrink: 0,
                      }}
                    />
                    <Box>
                      <Typography variant="body2" fontWeight="bold">{ev.title}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(ev.scheduledAt).toLocaleDateString("en-US", { month: "long", day: "numeric" })}
                        {" at "}
                        {new Date(ev.scheduledAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                        {ev.recurring && ev.recurring !== "none" && ` · ${ev.recurring}`}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }}>{ev.message}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </>
      )}

      {/* Add Event Dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {addType === "personal" ? "Add Personal Event" : "Add Group Reminder"}
          <IconButton
            onClick={() => setAddOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "12px !important" }}>
          <TextField
            label="Title"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            fullWidth
            size="small"
          />
          <TextField
            label="Message to send"
            value={form.message}
            onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
            fullWidth
            multiline
            rows={3}
            size="small"
          />
          <TextField
            label="Date"
            type="date"
            value={form.date}
            onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            fullWidth
            size="small"
          />
          <TextField
            label="Time"
            type="time"
            value={form.time}
            onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            fullWidth
            size="small"
          />
          {addType === "group" && (
            <FormControl size="small" fullWidth>
              <InputLabel>Recurring</InputLabel>
              <Select
                value={form.recurring}
                label="Recurring"
                onChange={(e) => setForm((f) => ({ ...f, recurring: e.target.value }))}
              >
                {RECURRING_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {formError && <Alert severity="error">{formError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button variant="contained" color="secondary" onClick={handleSaveEvent} disabled={saving}>
            {saving ? <CircularProgress size={18} color="inherit" /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Event Detail Dialog */}
      <Dialog open={!!detailEvent} onClose={() => setDetailEvent(null)} maxWidth="xs" fullWidth>
        {detailEvent && (
          <>
            <DialogTitle>
              {detailEvent.title}
              <IconButton
                onClick={() => setDetailEvent(null)}
                sx={{ position: "absolute", right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {new Date(detailEvent.scheduledAt).toLocaleDateString()} at {formatTime(detailEvent.scheduledAt)}
                {detailEvent.recurring && detailEvent.recurring !== "none" && ` · ${detailEvent.recurring}`}
              </Typography>
              <Typography variant="body2">{detailEvent.message}</Typography>
              <Chip
                label={detailEvent.type === "group" ? "Group Reminder" : "Personal"}
                size="small"
                sx={{
                  alignSelf: "flex-start",
                  backgroundColor: detailEvent.type === "group" ? "#2e7d32" : "#1565c0",
                  color: "#fff",
                }}
              />
              {deleteError && <Alert severity="error">{deleteError}</Alert>}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setDetailEvent(null)}>Close</Button>
              {detailEvent.createdBy === user.id && (
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDelete}
                  disabled={deleting}
                >
                  {deleting ? <CircularProgress size={18} color="inherit" /> : "Delete"}
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};
